import {
  Avatar,
  Button,
  Flex,
  Loader,
  PasswordInput,
  TextInput,
} from "@mantine/core";
import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import styled from "styled-components";
import { useForm } from "@mantine/form";
import {
  combineValidators,
  validateMinLength,
  validateNoSpaces,
  validatePhoneNumberIsNumeric,
  validatePhoneNumberStartsWithZero,
  validateRequired,
} from "../utils/validations";
import { formatTerminalCode } from "../utils/textUtils";

const normalizePhone = (phone: string): string => {
  if (!phone) return "";
  if (phone.startsWith("0")) return "+90" + phone.slice(1);
  if (phone.startsWith("90")) return "+" + phone;
  return phone;
};

const LoginPage = () => {
  const { login } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  const form = useForm({
    initialValues: {
      companyCode: "",
      phone: "",
      password: "",
    },
    validate: {
      companyCode: validateRequired,
      phone: combineValidators(
        validateRequired,
        validatePhoneNumberStartsWithZero,
        validatePhoneNumberIsNumeric,
        validateNoSpaces,
        validateMinLength(11)
      ),
      password: combineValidators(validateRequired, validateNoSpaces),
    },
  });

  const handleSubmit = async ({ phone, password, companyCode }: any) => {
    const formattedPhone = normalizePhone(phone);
    setIsLoading(true);
    try {
      await login(`${formattedPhone}`, `${password}`, `${companyCode}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <LoginPageWrapper>
      <Flex gap={35} align={"center"} justify={"center"} direction={"column"}>
        <LogoWrapper $isLoading={isLoading}>
          <Avatar src={"/assets/kronifyLogo.png"} size={60} />
          <LogoText>Kronify</LogoText>
        </LogoWrapper>
        {isLoading && (
          <Flex gap={5} align={"center"}>
            <Loader color="#5c26ff" size="md" type="oval" />
          </Flex>
        )}
      </Flex>

      <LoginFormWrapper
        $isVisible={!isLoading}
        as="form"
        onSubmit={form.onSubmit(handleSubmit)}
      >
        <LoginText>Giriş Yap</LoginText>
        <TextInput
          label="Şirket Kodu"
          placeholder="Şirket kodunu giriniz."
          radius={10}
          size="md"
          {...form.getInputProps("companyCode")}
          onChange={(event) => {
            form.setFieldValue(
              "companyCode",
              formatTerminalCode(event?.target?.value)
            );
          }}
        />
        <TextInput
          label="Telefon Numarası"
          placeholder="05*********"
          radius={10}
          size="md"
          maxLength={11}
          {...form.getInputProps("phone")}
        />
        <PasswordInput
          label="Şifre"
          placeholder="*****"
          radius={10}
          size="md"
          maxLength={16}
          {...form.getInputProps("password")}
        />
        <Button type="submit" fullWidth h={44} radius={10} color="#5C26FF">
          <Flex gap={12} align={"center"} justify={"center"}>
            <ButtonLabel>Giriş Yap</ButtonLabel>
            <Avatar src={"/assets/arrowIcon.png"} size={24} radius={0} />
          </Flex>
        </Button>
      </LoginFormWrapper>
    </LoginPageWrapper>
  );
};

export default LoginPage;

const LoginPageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 93px;
  height: 100vh;
  transition: gap 0.5s ease;
`;

const LoginFormWrapper = styled.div<{ $isVisible: boolean }>`
  width: 380px;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  max-height: ${({ $isVisible }) => ($isVisible ? "500px" : "0px")};
  overflow: hidden;
  transition: max-height 0.5s ease;
`;

const LogoWrapper = styled.div<{ $isLoading: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: margin-top 0.5s ease;
  margin-top: ${({ $isLoading }) => ($isLoading ? "80px" : "0px")};
`;

const LogoText = styled.div`
  color: #5c26ff;
  font-size: 60px;
  font-weight: 600;
  line-height: 45px;
`;

const LoginText = styled.div`
  display: flex;
  align-self: center;
  font-size: 28px;
  font-weight: 600;
  line-height: 24px;
  color: black;
`;

const ButtonLabel = styled.label`
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
`;
